// P-01 Player Directory: the normative schema.
// See docs/design/interfaces/ICD-P01-player-directory.md.
//
// Who exists, what position they play, who they play for, and whether they can
// score. Every other provider's ids resolve against this one, and every roster,
// lineup, trade and scored stat line stores an id minted here.
//
// The one rule this file exists to enforce, from the ICD's purpose:
//
//   the identity survives replacing the source the names came from
//
// So there are two shapes, not one. A provider emits `SourcePlayer`, keyed the
// way its vendor keys things. Consumers read `DirectoryPlayer`, keyed the way
// we key things. The registry (./registry.ts) is the piece in between, and it
// is the only code that ever sees both.
//
// Pure and sport-free: positions, injury statuses and pro teams are ids looked
// up in the SportDefinition (E-01), never compared to literals here.

import { SHORT_NAME_MAX } from '@fsp/info';
import { z } from 'zod';

export type { PracticeLevel } from '@fsp/info';

const Iso = z.string();

/** An injury designation. `status` is an `InjuryStatusDef.id` in the sport definition. */
export const DirectoryInjury = z.object({
  status: z.string(),
  note: z.string().nullable(),
  /**
   * When the injury report this designation is from was first read as it
   * stands (B-52): the source carries no filing date. Null for a designation
   * his club's roster gives (injured reserve, PUP), which no report files, and
   * in a document built before it was kept. Added 2026-11-01 (0099).
   */
  readAt: Iso.nullable().default(null),
});
export type DirectoryInjury = z.infer<typeof DirectoryInjury>;

/**
 * Whether a player can appear in a game at all. Not an injury — a healthy
 * player cut on Tuesday is `inactive`.
 */
/**
 * How long a short name may be: a hard cap, held by the projection that writes
 * the row as well as by this shape, so it lives below both (`@fsp/info`).
 */
export { SHORT_NAME_MAX };

export const DirectoryStatus = z.enum(['active', 'inactive', 'practice-squad', 'retired']);
export type DirectoryStatus = z.infer<typeof DirectoryStatus>;

// ── What consumers read (ICD §6.1) ────────────────────────────────────────

export const DirectoryPlayer = z.object({
  /**
   * Ours. Minted by the registry, permanent, never reused, and never derived
   * from a vendor key — that is invariant 1, and it is the whole reason a
   * source can be replaced. Shaped `<sport>:<opaque>`.
   */
  id: z.string(),
  /** Which sport's vocabulary the rest of this record speaks. */
  sport: z.string(),
  firstName: z.string().min(1).max(60),
  lastName: z.string().min(1).max(60),
  /** What a player card shows — the source's own display spelling, suffixes and all. */
  displayName: z.string().min(1).max(80),
  /**
   * What a roster row shows when space is short ("P. Mahomes"). Stored rather
   * than derived because "first initial, last name" is a football convention,
   * not a universal one. Null means the sport definition derives it.
   */
  shortName: z.string().max(SHORT_NAME_MAX).nullable(),
  /** Every position this player may fill; order is significance, first is primary. */
  positions: z.array(z.string()).min(1),
  /**
   * A `ProTeam.id`. Null means **not on a pro roster** — a free agent. It never
   * means "we could not tell"; not knowing is a failure, not a null (§9).
   */
  proTeam: z.string().nullable(),
  status: DirectoryStatus,
  /** Null means no injury designation, which is not the same as "confirmed healthy". */
  injury: DirectoryInjury.nullable(),
  /**
   * 0 is a rookie. Drives the rookie badge, a dynasty's rookie draft pool and
   * who the taxi squad takes (`qualifiesForTaxi`); a player whose source gives
   * no seasons is stored as 0, a rookie.
   */
  experienceYears: z.number().int().min(0),
  /**
   * His place on his club's depth chart (0083): the chart's column he is
   * listed in, in its own word (`FB`, `PK`, `RCB`), among the columns the
   * sport maps to his first position, and his rank there, 1 the first string —
   * his best when he is in several. Null on no current chart, or the chart of
   * a club he is not rostered by.
   */
  depth: z
    .object({ position: z.string(), rank: z.number().int().min(1) })
    .nullable()
    .default(null),
  /**
   * Every vendor key we have ever seen for this person, by source id. **The
   * swap mechanism**: a new source's rows find their way home through this map.
   */
  externalIds: z.record(z.string(), z.string()),
  /** When a source last confirmed this record. Drives staleness. */
  asOf: Iso,
});
export type DirectoryPlayer = z.infer<typeof DirectoryPlayer>;

// ── What a provider emits (ICD §6.2) ──────────────────────────────────────

export const SourcePlayer = z.object({
  /** Which implementation emitted this. */
  sourceId: z.string(),
  /**
   * The vendor's own id, in the vendor's own format. Lives only in
   * `externalIds`; it never becomes, and never contributes to, a canonical id.
   */
  sourceKey: z.string().min(1).max(128),
  sport: z.string(),
  firstName: z.string().min(1).max(60),
  lastName: z.string().min(1).max(60),
  displayName: z.string().min(1).max(80),
  shortName: z.string().max(SHORT_NAME_MAX).nullable(),
  positions: z.array(z.string()).min(1),
  proTeam: z.string().nullable(),
  status: DirectoryStatus,
  injury: DirectoryInjury.nullable(),
  experienceYears: z.number().int().min(0),
  /**
   * The only reliable disambiguator between two people with the same name and
   * position — the NFL has fielded two Josh Allens at once. Optional: its
   * absence downgrades a match rather than blocking one (invariant 7).
   */
  birthDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  /** Cross-source keys the vendor happens to publish, by source id. */
  externalIds: z.record(z.string(), z.string()),
});
export type SourcePlayer = z.infer<typeof SourcePlayer>;

// ── The fast half (ICD §6.3) ──────────────────────────────────────────────

/**
 * A sparse update. A field left `null` means "not this time", never "cleared" —
 * the three-way on injury and pro team is deliberate, because collapsing them
 * leaves players Questionable forever.
 */
export const StatusChange = z.object({
  sourceId: z.string(),
  sourceKey: z.string().min(1).max(128),
  /** When the source showed this state, not when we polled. */
  observedAt: Iso,
  changedStatus: DirectoryStatus.nullable(),
  changedInjury: z.union([DirectoryInjury, z.literal('cleared'), z.null()]),
  changedProTeam: z.union([z.string(), z.literal('released'), z.null()]),
});
export type StatusChange = z.infer<typeof StatusChange>;

// ── Batches and the failure envelope (ICD §4, §9) ─────────────────────────

export const DirectorySnapshot = z.object({
  players: z.array(SourcePlayer),
  fetchedAt: Iso,
  /**
   * True when `players` is everyone this source knows. When false, absence
   * carries no information at all — invariant 4, which is what stops a
   * truncated download from retiring the NFL.
   */
  complete: z.boolean(),
});
export type DirectorySnapshot = z.infer<typeof DirectorySnapshot>;

export const StatusBatch = z.object({
  changes: z.array(StatusChange),
  /** Opaque to the consumer. Resuming misses no change and repeats none. */
  next: z.string().nullable(),
  fetchedAt: Iso,
});
export type StatusBatch = z.infer<typeof StatusBatch>;

/**
 * Why a fetch did not produce data. `drift` is the loud one: it means the
 * source answered in a shape we do not recognize, or answered with something
 * that cannot be true — an empty directory, or one that shrank by more than a
 * tenth. Both of those are facts about us, never about football.
 */
export const DirectoryFailureCode = z.enum([
  'unavailable',
  'drift',
  'rate_limited',
  'unauthorized',
  'partial',
]);
export type DirectoryFailureCode = z.infer<typeof DirectoryFailureCode>;

export interface DirectoryFailure {
  code: DirectoryFailureCode;
  message: string;
  /** Records dropped while the rest parsed, for `partial`. */
  dropped?: number;
}

/** The provider envelope. Never optional: empty and unknown are different values. */
export type DirectoryResult<T> = { ok: true; value: T } | { ok: false; failure: DirectoryFailure };

// ── The provider interface (ICD §4) ───────────────────────────────────────

/**
 * Two operations, because the directory has a slow half and a fast half with
 * different terms. Sleeper's documented ask is that the 5 MB player file be
 * fetched at most once a day, while an injury designation can change ninety
 * minutes before kickoff and decide someone's week. One operation would have to
 * either break the terms or be a day stale.
 */
export interface PlayerDirectoryProvider {
  /** Stable identifier for this implementation; recorded on every record it emits. */
  readonly sourceId: string;
  /** Everyone this source knows about, as one set. Expensive; daily. */
  snapshot(): Promise<DirectoryResult<DirectorySnapshot>>;
  /** Status and injury movement since a cursor. Cheap; hourly, faster near kickoff. */
  statusesSince(cursor: string | null): Promise<DirectoryResult<StatusBatch>>;
}
